import { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

/* =====================================================
   MODAL
===================================================== */
function ConstructorModal({ open, onClose, onSave, team, season }) {
  const [form, setForm] = useState({
    position: "",
    name: "",
    engine: "",
    points: 0,
    movement: "same",
  });

  useEffect(() => {
    if (!open) return;

    if (team) {
      setForm({
        position: String(team.position),
        name: team.name,
        engine: team.engine,
        points: team.points,
        movement: team.movement,
      });
    } else {
      setForm({
        position: "",
        name: "",
        engine: "",
        points: 0,
        movement: "same",
      });
    }
  }, [open, team]);

  if (!open) return null;

  const validate = () => {
    if (!season) return "Season not selected";
    if (!form.position || Number(form.position) <= 0)
      return "Position is required";
    if (!form.name.trim()) return "Constructor name is required";
    return null;
  };

  const submit = () => {
    const error = validate();
    if (error) {
      alert(error);
      return;
    }

    onSave({
      season,
      position: Number(form.position),
      name: form.name.trim(),
      engine: form.engine.trim(),
      points: Number(form.points),
      movement: form.movement,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-neutral-900 w-full max-w-md rounded-xl border border-neutral-800 p-6">
        <h3 className="text-lg font-semibold mb-4">
          {team ? "Edit Constructor" : "Add Constructor"}
        </h3>

        <div className="space-y-3">
          <input
            type="number"
            placeholder="Position"
            value={form.position}
            onChange={(e) =>
              setForm({ ...form, position: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            placeholder="Constructor Name"
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            placeholder="Engine"
            value={form.engine}
            onChange={(e) =>
              setForm({ ...form, engine: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <input
            type="number"
            placeholder="Points"
            value={form.points}
            onChange={(e) =>
              setForm({ ...form, points: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          />

          <select
            value={form.movement}
            onChange={(e) =>
              setForm({ ...form, movement: e.target.value })
            }
            className="w-full bg-neutral-950 border border-neutral-800 px-3 py-2 rounded-lg"
          >
            <option value="same">— Same</option>
            <option value="up">↑ Up</option>
            <option value="down">↓ Down</option>
          </select>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="text-neutral-400">
            Cancel
          </button>
          <button
            onClick={submit}
            className="bg-red-600 hover:bg-red-500 px-4 py-2 rounded-lg text-white font-semibold"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   MAIN
===================================================== */
export default function ConstructorsStandings({ admin }) {
  const [seasons, setSeasons] = useState([]);
  const [activeSeason, setActiveSeason] = useState("");
  const [teams, setTeams] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  /* LOAD SEASONS */
  useEffect(() => {
    return onSnapshot(collection(db, "standings_constructors"), (snap) => {
      const yrs = [
        ...new Set(snap.docs.map((d) => d.data().season)),
      ].sort((a, b) => b - a);

      setSeasons(yrs);
      if (!activeSeason && yrs.length) setActiveSeason(yrs[0]);
    });
  }, [activeSeason]);

  /* LOAD TEAMS */
  useEffect(() => {
    if (!activeSeason) {
      setTeams([]);
      return;
    }

    return onSnapshot(
      query(
        collection(db, "standings_constructors"),
        where("season", "==", activeSeason)
      ),
      (snap) => {
        setTeams(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) => a.position - b.position)
        );
      }
    );
  }, [activeSeason]);

  /* SAVE */
  const saveTeam = async (data) => {
    if (editingTeam) {
      await updateDoc(
        doc(db, "standings_constructors", editingTeam.id),
        {
          ...data,
          updatedAt: serverTimestamp(),
          updatedBy: admin.email,
        }
      );
    } else {
      await addDoc(collection(db, "standings_constructors"), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: admin.email,
      });
    }

    setModalOpen(false);
    setEditingTeam(null);
  };

  return (
    <div className="bg-neutral-900 rounded-xl p-6 border border-neutral-800">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold">Constructors Standings</h2>

        <div className="flex gap-3 items-center">
          <select
            value={activeSeason}
            onChange={(e) => setActiveSeason(e.target.value)}
            className="h-10 w-32 bg-neutral-950 border border-neutral-800 px-4 rounded-lg text-sm"
          >
            <option value="">Select season</option>
            {seasons.map((y) => (
              <option key={y}>{y}</option>
            ))}
          </select>

          <button
            disabled={!activeSeason}
            onClick={() => setModalOpen(true)}
            className={`h-10 px-4 rounded-lg font-semibold transition ${
              activeSeason
                ? "bg-red-600 hover:bg-red-500 text-white"
                : "bg-neutral-700 text-neutral-400 cursor-not-allowed"
            }`}
          >
            + Add Constructor
          </button>
        </div>
      </div>

      {/* TABLE */}
      {teams.length === 0 ? (
        <div className="text-sm text-neutral-500 text-center py-6">
          {seasons.length === 0
            ? "No constructors yet. Add the first constructor to create a season."
            : "No constructors for this season."}
        </div>
      ) : (
        <div className="space-y-2">
          {teams.map((t) => (
            <div
              key={t.id}
              className="grid grid-cols-6 gap-2 bg-neutral-950 p-3 rounded-lg items-center"
            >
              <div>{t.position}</div>
              <div>{t.name}</div>
              <div>{t.engine}</div>
              <div>{t.points} pts</div>
              <div>{t.movement}</div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setEditingTeam(t);
                    setModalOpen(true);
                  }}
                  className="text-neutral-400 hover:text-white"
                >
                  Edit
                </button>
                <button
                  onClick={() =>
                    deleteDoc(
                      doc(db, "standings_constructors", t.id)
                    )
                  }
                  className="text-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConstructorModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingTeam(null);
        }}
        onSave={saveTeam}
        team={editingTeam}
        season={activeSeason}
      />
    </div>
  );
}
